// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  Check as CheckIcon,
  Close as CloseIcon,
  WarningStatusIcon,
} from '@/constants/hardfin-standard-icons';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { ReactNode } from 'react';

export interface Phase<TStatus extends string> {
  status: TStatus;
  label: string;
  number: number;
  /**
   * When true, the active phase renders as an unfilled circle (blue border,
   * number) instead of a filled circle with a check — indicating the phase
   * has been partially but not fully completed.
   */
  isPartial?: boolean;
  /**
   * Optional subtext to display below the phase label.
   * Can be a ReactNode (e.g., a date wrapped in MultiTimezoneTooltip).
   */
  subtext?: ReactNode;
  warningTooltip?: string;
  tooltip?: ReactNode;
}

interface PhaseIndicatorProps<TStatus extends string> {
  phases: Phase<TStatus>[];
  currentStatus: TStatus;
  terminalStatuses: readonly TStatus[];
  completedStatuses?: readonly TStatus[];
  /**
   * Optional cancelled status. If not provided, cancelled state handling is disabled.
   */
  cancelledStatus?: TStatus;
  cancelledReason?: string | null;
  /**
   * Label shown under the last phase when the indicator is in the cancelled
   * state. Defaults to "Cancelled"; invoice uses "Void" for the same visual
   * treatment.
   */
  cancelledLabel?: string;
}

/**
 * Generic phase indicator component that displays a stepper-style workflow.
 * Shows completed phases with checkmarks, current phase highlighted, and
 * optionally handles cancelled state with an X icon and tooltip.
 */
export function PhaseIndicator<TStatus extends string>(
  props: PhaseIndicatorProps<TStatus>
) {
  // Find the current phase index
  const currentPhaseIndex = props.phases.findIndex(
    (phase) => phase.status === props.currentStatus
  );

  const isCancelled =
    props.cancelledStatus !== undefined &&
    props.currentStatus === props.cancelledStatus;
  const isTerminal = props.terminalStatuses.includes(props.currentStatus);
  const completedStatusSet = props.completedStatuses
    ? new Set(props.completedStatuses)
    : null;

  return (
    <Box sx={{ mb: 2, mt: 1, px: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '20px',
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'divider',
            zIndex: 0,
          },
        }}
      >
        {props.phases.map((phase, index) => {
          const isActive = index === currentPhaseIndex;
          const isCompleted = completedStatusSet
            ? completedStatusSet.has(phase.status) && !isActive
            : index < currentPhaseIndex;
          const isLast = index === props.phases.length - 1;
          const showTerminalIcon = isLast && isTerminal && !phase.isPartial;
          const isFilled = completedStatusSet
            ? completedStatusSet.has(phase.status)
            : isCompleted || (isActive && !phase.isPartial);
          const cancelledTooltip =
            isLast && isCancelled && Boolean(props.cancelledReason);
          const tooltipTitle =
            phase.tooltip ??
            (cancelledTooltip ? props.cancelledReason : undefined);
          const showTooltip = Boolean(tooltipTitle);

          const labelText =
            isLast && isCancelled
              ? (props.cancelledLabel ?? 'Cancelled')
              : phase.label;
          const labelColor =
            isActive && !isCancelled ? 'primary.main' : 'text.secondary';
          const labelNode = (
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="center"
              spacing={0.5}
              sx={showTooltip ? { cursor: 'help' } : undefined}
            >
              <Typography
                variant="body2"
                component="span"
                sx={{
                  fontWeight:
                    isActive || (isLast && isCancelled) ? 'bold' : 'normal',
                  color: labelColor,
                  textAlign: 'center',
                }}
              >
                {labelText}
              </Typography>
              {phase.warningTooltip && (
                <Tooltip arrow title={phase.warningTooltip}>
                  <WarningStatusIcon
                    aria-label={`${labelText} serial confirmation warning`}
                    fontSize="small"
                    sx={{ color: 'warning.main' }}
                  />
                </Tooltip>
              )}
            </Stack>
          );

          return (
            <Box
              key={phase.status}
              sx={{
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: 'background.paper',
                px: 1,
              }}
            >
              {/* Phase circle */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor:
                    showTerminalIcon && isCancelled
                      ? 'error.main'
                      : isFilled || showTerminalIcon
                        ? 'primary.main'
                        : 'background.paper',
                  border: (theme) =>
                    `2px solid ${
                      showTerminalIcon && isCancelled
                        ? theme.palette.error.main
                        : isCompleted || showTerminalIcon
                          ? theme.palette.primary.main
                          : isActive
                            ? theme.palette.primary.main
                            : theme.palette.divider
                    }`,
                  color:
                    isFilled || showTerminalIcon
                      ? 'white'
                      : isActive
                        ? 'primary.main'
                        : 'text.secondary',
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                {showTerminalIcon ? (
                  isCancelled ? (
                    <CloseIcon />
                  ) : (
                    <CheckIcon />
                  )
                ) : isFilled ? (
                  <CheckIcon />
                ) : (
                  <Typography variant="body2">{phase.number}</Typography>
                )}
              </Box>

              {/* Phase label — tooltips anchor here so they appear under the
                  status label rather than the full phase container. */}
              {showTooltip ? (
                <Tooltip arrow title={tooltipTitle}>
                  {labelNode}
                </Tooltip>
              ) : (
                labelNode
              )}

              {/* Optional subtext (e.g., date) */}
              {phase.subtext && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                    mt: 0.5,
                  }}
                >
                  {phase.subtext}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
